import { executeSql, isDBReady, saveClientOptions, getDeletedImages, permanentlyDeleteImage, getOrderById } from './db';
import { syncAssignedOrders, uploadImage, syncOrder, fetchClientOrderOptions, deleteImageOnServer } from './api';
import type { Order } from '../models/order';
import type { APIResult } from '../models/APIResult';
import type { ClientSyncResult } from '../models/clientSyncResult';
import { ORDER_STATUS_APPROVED, ORDER_STATUS_CANCELLED, ORDER_STATUS_COMPLETED, ORDER_STATUS_SURVEYOR_PENDING, ORDER_STATUS_REJECTED, ORDER_STATUS_SUBMITTED, SALESREP_ROLE, SURVEYOR_ROLE } from '../constants';
import { showConfirmationModal, type ModalButton } from '../components/confirmation-modal';
import { getUserRole } from './auth';

interface OrderRow {
  id: number;
  salesrep_id: number;
  status: string;
  step_data: string;
  created_at: string;
  updated_at: string;
  synced_at: string | null;
}

interface ImageRow {
  id: number;
  order_id: number;
  step: number;
  blob: Uint8Array;
  filename: string;
  synced_at: string | null;
  tag: string;
}

export async function performServerSync() {
  console.log('Starting syncOrders...');
  if (!navigator.onLine) {
    console.log('syncOrders failed. Offline mode.');
    return;
  }

  if (!isDBReady()) {
    console.log('syncOrders failed. db not ready.');
    console.error('syncOrders failed. Database not initialized');
    return;
  }

  const orders = await executeSql<OrderRow>(
    'SELECT * FROM orders', //WHERE status = ? AND synced_at IS NULL
    [],
    //['completed'],
    true
  ) as OrderRow[] | null;
  
  if (!orders) {
    console.log('no orders locally.');
    //return;
  }

  let localOrderCount: number = 0;

  if(orders) {

    const uRole = await getUserRole();

    for(let li = 0; li < orders.length; li++) {
      if(orders[li].status !== ORDER_STATUS_COMPLETED 
        && orders[li].status !== ORDER_STATUS_CANCELLED
        ) {

        
        if(uRole === SALESREP_ROLE && orders[li].status === ORDER_STATUS_SUBMITTED) {
          localOrderCount++;
        }

        if(uRole === SURVEYOR_ROLE 
          && (orders[li].status !== ORDER_STATUS_SURVEYOR_PENDING) 
          && (orders[li].status === ORDER_STATUS_APPROVED || orders[li].status === ORDER_STATUS_REJECTED)) {
          localOrderCount++;
        }


      }
    }
  }

  let orderUploaded = 0;

  let ModalButtons: ModalButton[] = [];

  if(localOrderCount > 0) {
    ModalButtons.push({ label: 'Upload Orders', value: 'upload', style: 'primary' });
  }
  ModalButtons.push({ label: 'Refresh Orders', value: 'download_orders', style: 'secondary' });
  ModalButtons.push({ label: 'Refresh Options', value: 'download_options', style: 'secondary' });
  ModalButtons.push({ label: 'Cancel', value: 'cancel', style: 'secondary' });

  const uploadMessage = localOrderCount > 0 ? `You have ${localOrderCount} local order to sync.<br />` : '';
  const uploadTip = localOrderCount > 0 ? `Upload Orders - Send order changes to the server.<br />` : '';
  const uploadResult = await showConfirmationModal({
    title: 'Online Sync',
    message: `Connect to server. <br />
    ${uploadMessage} 
    ${uploadTip} 
    [Refresh Orders] - download the latest from the server.<br />
    [Refresh Options] - downloads options for selection.
    `,
    modalType: 'info',
    buttons: ModalButtons
  });
  
  if (uploadResult === 'upload') {
    orderUploaded = await uploadOrdersToServer(orders || []);
    console.log('[closeOnlineSync] uploadOrdersToServer:', orderUploaded);
  } 
  else if(uploadResult === 'download_options') {
    //fetch any edits from server
    console.log('Sync Options from server.');
    await syncClientOptions();
    console.log('Sync Options completed successfully');
  }
  else if(uploadResult === 'download_orders') {
    //fetch any edits from server
    console.log('getting edits from server.');
    await syncAssignedOrders();
  }
  else {
    // User cancelled - stay in wizard
    console.log('[closeOnlineSync] User cancelled, staying in wizard');
    return;
  }
  
  

  

  

}

export async function uploadOrdersToServer(orders: OrderRow[]): Promise<number> {

  let orderToUploadCount = 0;
  let successfulUploads = 0;
  console.log('Syncing local orders to server...', orders);
  //if we have orders to sync send one by one
  if(orders && orders.length > 0) {
    for (const order of orders) {
      try {

        const orderData: Order | null  = await getOrderById(order.id);

        if (!orderData) {
          console.error(`[syncOrders] Order data not found for order ID: ${order.id}`);
          //TODO mark the failuer sync status.
          continue; // Skip to next order
        }

        if(orderData.status !== ORDER_STATUS_SUBMITTED 
          && orderData.status !== ORDER_STATUS_APPROVED
          && orderData.status !== ORDER_STATUS_REJECTED) {
          console.log(`[syncOrders] Order ID: ${order.id} is submitted. Skipping.`);
          continue;
        }
        
        orderToUploadCount++;

        const syncedOrderResult: APIResult<ClientSyncResult> = await syncOrder(orderData);
        console.log('Order synced:', syncedOrderResult);

        if(syncedOrderResult.success !== true) {
          console.error(`[syncOrders] Order ID: ${order.id} sync failed on server:`, syncedOrderResult.message);
          continue; // Skip to next order
        }
        else {
          successfulUploads++;
        }

        // Update local order with sync result
        const syncResultData = syncedOrderResult.data;
        await executeSql(
          'UPDATE orders SET status = ?, sync_version = ?, sync_status = ?, last_modified = ? WHERE id = ?',
          [syncResultData.order_status, syncResultData.sync_version, syncResultData.sync_status, syncResultData.sync_stamp, order.id]
        );

        // Upload images
        const images = await executeSql<ImageRow>(
          'SELECT * FROM images WHERE order_id = ? AND synced_at IS NULL AND deleted_at IS NULL',
          [order.id],
          true
        ) as ImageRow[] | null;
        
        if (images) {
          for (const image of images) {
            // Upload image and get server ID back
            const serverId = await uploadImage(image);
            console.log(`[syncOrders] Image ${image.id} uploaded, server_id: ${serverId}`);
            
            // Update local image with server_id and synced_at
            await executeSql(
              `UPDATE images SET synced_at = datetime('now'), server_id = ? WHERE id = ?`,
              [serverId, image.id]
            );
          }
        }
        
        // Delete images that were removed locally
        const deletedImages = await getDeletedImages();
        if (deletedImages && deletedImages.length > 0) {
          console.log(`[syncOrders] Found ${deletedImages.length} images to delete on server`);
          for (const image of deletedImages) {
            try {
              await deleteImageOnServer(image.server_id);
              console.log(`[syncOrders] Deleted image ${image.server_id} on server`);
              
              // Permanently delete from local database after successful server deletion
              await permanentlyDeleteImage(image.id);
            } catch (error) {
              console.error(`[syncOrders] Failed to delete image ${image.server_id} on server:`, error);
              // Continue with other deletions even if one fails
            }
          }
        }
        
        // Mark order as synced
        await executeSql(
          `UPDATE orders SET synced_at = datetime('now') WHERE id = ?`,
          [order.id]
        );


      } catch (error) {
        console.error('Sync failed:', error);
        // Retry later with Background Sync
        if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
          navigator.serviceWorker.ready.then((reg) => {
            // Type assertion needed as SyncManager is not in default TypeScript lib
            (reg as any).sync.register('sync-orders');
          });
        }
      }
    }
  }

  //console.log('Local orders synced.');

  return successfulUploads;
}


export async function syncClientOptions() {
  //console.log('Starting syncClientOptions...');
  if (!navigator.onLine) {
    console.log('syncClientOptions failed. Offline mode.');
    return;
  }

  if (!isDBReady()) {
    console.log('syncClientOptions failed. db not ready.');
    console.error('syncClientOptions failed. Database not initialized');
    return;
  }

  try {
    // Fetch latest options from server
    //console.log('Fetching client options from server...');

    const optionsDataResult = await fetchClientOrderOptions();
    //console.log('Client options fetched:', optionsDataResult);

    // Validate response structure
    if (!optionsDataResult?.success || !optionsDataResult?.data?.field_products) {
      throw new Error('Invalid client options data structure');
    }

    

    // Save client options and catalogue to database
    await saveClientOptions(optionsDataResult.data);
  } catch (error) {
    console.error('Failed to sync client options:', error);
    // Don't throw - allow app to continue with cached options if available
  }
}
